import { loadingState } from '../core/loadingState.js';
/**
 * settings-integration-detail-route.js — one provider's dedicated page
 * (/settings/integrations/:provider). Data-driven from the shared PROVIDERS
 * descriptors; this single module serves all four providers.
 *
 * Sections: back link -> hero (logo, status, identity, primary action) ->
 * setup steps (numbered stepper with done-states; collapses to a "Setup
 * guide" disclosure once connected) -> provider tools (pickers / event types /
 * trackers / sync) -> health -> danger zone (disconnect).
 *
 * Routing notes (see main.js): the registration publishes the PARENT route
 * name 'settings/integrations' so the Settings sub-nav keeps the Integrations
 * tab active; unknown slugs and portfolio mode bounce to the hub.
 *
 * Personas: manage-capable users get every control; read-only members get
 * status + health with zero buttons (canManage() gates every affordance).
 */

import {
  PROVIDERS, URL_TO_SLUG, WALKTHROUGHS, esc, toast, apiBase, emptyState, alertHTML,
  fetchIntegrations, connFor, statusOf, statusPill, logoHTML, ensureStyles, canManage,
  connectProvider, disconnectProvider, syncProvider, openProviderPicker,
  openCallrailSetup, openCalcomSetup, openGhlSetup, syncCallrail, takePendingReturn,
} from '../services/integrations-shared.js';
import { inPortfolio, ensurePortfolioStyles } from '../services/portfolio-view.js';
import { capList } from '../core/listExpand.js';
import { guardedFetch } from '../core/navGeneration.js';
import { mountLeadConnectionReturn, clearLeadBootRoute } from '../core/leadWorkspaceMemory.js';

const state = { provider: null, conn: null, configured: false, status: 'idle' };

export function render({ provider } = {}) {
  ensureStyles();
  ensurePortfolioStyles();
  const slug = URL_TO_SLUG[provider];
  // Blocked (unknown slug / portfolio mode): render an empty shell and let
  // init() do the ONE bounce. Navigating here too would push twice and trap
  // the Back button on the blocked URL.
  if (!slug || inPortfolio()) return '<div class="page-shell"></div>';
  const p = PROVIDERS[slug];
  state.provider = p;
  return `
    <div class="page-shell" data-section="settings-integrations">
      <a class="intg-back" href="#/settings/integrations"><span class="material-symbols-outlined" aria-hidden="true" style="font-size:16px">arrow_back</span>All integrations</a>
      <div id="intg-detail">
        <div class="dashboard-card intg-card"><div class="card-body">${loadingState('detail', 'Loading connection settings')}</div></div>
      </div>
    </div>`;
}

export async function init({ provider } = {}) {
  clearLeadBootRoute();
  const slug = URL_TO_SLUG[provider];
  if (!slug || inPortfolio()) {
    // Single bounce, replaceState: the blocked URL leaves history entirely so
    // Back lands wherever the user actually came from.
    window.router?.navigate('/settings/integrations', { historyAPIMethod: 'replaceState' });
    return;
  }
  mountLeadConnectionReturn(document.querySelector('[data-section="settings-integrations"]'));
  await load();

  // Fresh OAuth return forwarded from the hub: the next step is choosing what
  // feeds reporting - put the picker straight in front of the user.
  const pending = takePendingReturn();
  if (pending?.connected && pending.slug === slug && state.provider?.picker && canManage()) {
    openProviderPicker(state.provider, { refresh: load });
  }
}

async function load() {
  const body = document.getElementById('intg-detail');
  if (!body || !state.provider) return;
  try {
    const { denied, providers, configured } = await fetchIntegrations();
    if (denied) {
      body.innerHTML = `<div class="dashboard-card intg-card"><div class="card-body">${emptyState('lock', 'Integrations are managed by workspace admins.')}</div></div>`;
      return;
    }
    state.conn = connFor(providers, state.provider.slug);
    state.configured = !!configured?.[state.provider.slug];
    state.status = statusOf(state.provider, state.conn, state.configured);
    body.innerHTML = detailHTML();
    wire();
  } catch (e) {
    body.innerHTML = `<div class="dashboard-card intg-card"><div class="card-body">${emptyState('error', 'Could not load this integration.')}</div></div>`;
  }
}

// ── sections ─────────────────────────────────────────────────────────────────
function detailHTML() {
  const p = state.provider;
  return [
    heroCard(p),
    stepsCard(p),
    toolsCard(p),
    healthCard(p),
    dangerCard(p),
  ].filter(Boolean).join('');
}

function identityLine(p) {
  const acct = state.conn?.external_account || {};
  if (state.status !== 'connected') return p.card.sub;
  if (p.slug === 'calendly') {
    const who = [acct.name, acct.email && `(${acct.email})`].filter(Boolean).join(' ');
    return who ? `Connected as ${who}.` : 'Connected.';
  }
  if (p.slug === 'callrail') {
    const scope = [acct.account_name, acct.company_name].filter(Boolean).join(' · ');
    return scope ? `Connected to ${scope}.` : 'Connected.';
  }
  if (p.slug === 'hubspot') {
    return acct.hub_domain ? `Connected to ${acct.hub_domain}.` : 'Connected.';
  }
  if (p.slug === 'zoho') {
    return acct.location ? `Connected to Zoho CRM (${String(acct.location).toUpperCase()} data center).` : 'Connected to Zoho CRM.';
  }
  if (p.slug === 'salesforce') {
    return acct.username ? `Connected as ${acct.username}.` : 'Connected.';
  }
  if (p.slug === 'gohighlevel') {
    return acct.location_name ? `Connected to ${acct.location_name}.` : 'Connected.';
  }
  if (p.slug === 'calcom') {
    return acct.username ? `Connected as ${acct.username}.` : 'Connected.';
  }
  if (p.slug === 'ms_bookings') {
    return acct.upn ? `Connected as ${acct.upn}.` : 'Connected.';
  }
  const n = p.card.reachableCount(acct);
  return n ? `${n} reachable ${p.slug === 'google_analytics' ? (n === 1 ? 'property' : 'properties') : (n === 1 ? 'account' : 'accounts')}.` : 'Connected.';
}

function heroCard(p) {
  let actions = '';
  if (canManage()) {
    if (p.slug === 'google_ads' && state.status === 'connected' && state.configured) {
      actions = `<button type="button" class="intg-btn" id="${p.card.ids.connect}"><span class="material-symbols-outlined" aria-hidden="true">refresh</span>Update Google access</button>`;
    }
    if (p.connect && (state.status === 'idle' || state.status === 'broken') && state.configured) {
      actions = `<button type="button" class="intg-btn primary" id="${p.card.ids.connect}"><span class="material-symbols-outlined" aria-hidden="true">link</span>${state.status === 'broken' ? 'Reconnect' : 'Connect'}</button>`;
    }
    if ((p.slug === 'callrail' || p.slug === 'calcom' || p.slug === 'gohighlevel') && state.status !== 'connected') {
      actions = `<button type="button" class="intg-btn primary" id="${p.card.ids.connect}"><span class="material-symbols-outlined" aria-hidden="true">link</span>${state.status === 'broken' ? 'Reconnect' : 'Connect'}</button>`;
    }
  }
  // One description per state: unavailable explainer / broken explainer /
  // idle pitch (manage-capable only; read-only viewers cannot connect, so they
  // get the plain tagline) / connected identity.
  let desc;
  if (state.status === 'unavailable') desc = `${p.label} is being set up for this platform.`;
  else if (state.status === 'broken') desc = p.card.brokenDesc;
  else if (state.status === 'idle') desc = canManage() ? p.card.idleDesc : p.card.sub;
  else desc = identityLine(p);
  return `
    <div class="dashboard-card intg-card">
      <div class="card-body">
        <div class="intg-hero">
          ${logoHTML(p, { size: 'hero' })}
          <div class="intg-hero__meta">
            <div class="intg-hero__name-row">
              <span class="intg-hero__name">${esc(p.label)}</span>
              ${statusPill(state.status, state.conn)}
            </div>
            <div class="intg-hero__sub">${esc(p.card.sub)}</div>
          </div>
          <div class="intg-actions">${actions}</div>
        </div>
        <p class="intg-desc">${esc(desc)}</p>
        ${(p.slug === 'callrail' || p.slug === 'calcom' || p.slug === 'gohighlevel') && state.status !== 'connected' ? `<div id="${p.card.ids.setup}" style="display:none"></div>` : ''}
      </div>
    </div>`;
}

function stepsCard(p) {
  const w = WALKTHROUGHS[p.slug];
  if (!w) return '';
  // The live stepper only renders when its step 1 ("Click Connect...") is a
  // real next action: an available provider in front of a manage-capable
  // viewer. Connected, unavailable, and read-only all collapse to the guide.
  if (state.status === 'connected' || state.status === 'unavailable' || !canManage()) {
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <p class="intg-desc" style="margin-top:0">${esc(w.what)}</p>
          <details class="intg-walkthrough">
            <summary>Setup guide</summary>
            <div class="intg-walkthrough__body">
              <ol>${w.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
            </div>
          </details>
        </div>
      </div>`;
  }
  const currentIdx = 0; // not connected: step 1 is the live step
  return `
    <div class="dashboard-card intg-card">
      <div class="card-body">
        <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">checklist</span> Set up ${esc(p.label)}</div>
        <p class="intg-desc" style="margin:0 0 10px">${esc(w.what)}</p>
        <ol class="intg-steps">
          ${w.steps.map((s, i) => `
            <li class="intg-step${i === currentIdx ? ' intg-step--current' : ''}">
              <span class="intg-step__num">${i + 1}</span>
              <span class="intg-step__text">${esc(s)}</span>
            </li>`).join('')}
        </ol>
      </div>
    </div>`;
}

function toolsCard(p) {
  if (state.status !== 'connected') return '';
  const manage = canManage();

  if (p.picker) {
    const n = p.card.reachableCount(state.conn?.external_account);
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">tune</span> Reporting</div>
          <p class="intg-desc" style="margin:0">${n ? p.card.reachDesc(n) : p.card.noneDesc}</p>
          ${manage ? `
          <div class="intg-actions" style="justify-content:flex-start;margin-top:12px">
            <button type="button" class="intg-btn" id="${p.card.ids.choose}"><span class="material-symbols-outlined" aria-hidden="true">checklist</span>${p.card.chooseLabel}</button>
            <button type="button" class="intg-btn" id="${p.card.ids.sync}"><span class="material-symbols-outlined" aria-hidden="true">sync</span>Sync now</button>
          </div>` : ''}
          <div id="${p.card.ids.panel}" style="display:none"></div>
        </div>
      </div>
      ${p.slug === 'google_ads' && manage ? `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">flag</span> Conversions in reporting</div>
          <p class="intg-desc" style="margin:0 0 6px">Choose which Google Ads conversion actions count on Ad Performance, beside magnet leads. Counts stay Google's own numbers.</p>
          <div id="intg-g-convactions">${alertHTML('Loading conversion actions…')}</div>
        </div>
      </div>` : ''}`;
  }

  // Calendly event types + CallRail trackers hit metered external APIs, so
  // GET .../event_types and .../trackers stay MANAGE_MAGNETS-gated server-side
  // (unlike GET /integrations). Read-only viewers skip these sections entirely
  // rather than render a guaranteed 403 as an error alert.
  if (p.slug === 'calendly') {
    if (!manage) return '';
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">event_available</span> Booking</div>
          <p class="intg-desc" style="margin:0 0 6px">Turn on Calendly booking per magnet under Magnets, in the Book call-to-action. These event types are available:</p>
          <div id="intg-c-events">${alertHTML('Loading event types…')}</div>
        </div>
      </div>`;
  }

  // CRM destinations (HubSpot, Zoho) share one Lead sync section, data-driven
  // from the provider's `crm` copy. Sync status hits our own ledger but rides
  // the same manage-gated server route family; read-only viewers skip the
  // section like the others. Only one provider page renders at a time, so the
  // element ids stay static.
  if (p.crm) {
    if (!manage) return '';
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">sync_alt</span> Lead sync</div>
          <p class="intg-desc" style="margin:0 0 6px">${esc(p.crm.syncDesc)}</p>
          <div id="intg-crm-status">${alertHTML('Loading sync status…')}</div>
          <label class="intg-ca-row" style="margin-top:10px">
            <input type="checkbox" id="intg-crm-qualified">
            <span class="intg-ca-name">${esc(p.crm.qualifiedToggle)}</span>
          </label>
          <div class="intg-actions" style="justify-content:flex-start;margin-top:12px">
            <button type="button" class="intg-btn" id="${p.card.ids.sync}"><span class="material-symbols-outlined" aria-hidden="true">sync</span>Sync now</button>
          </div>
        </div>
      </div>`;
  }

  if (p.slug === 'ms_bookings') {
    if (!manage) return '';
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">event_note</span> Booking pages</div>
          <p class="intg-desc" style="margin:0 0 6px">Point any magnet Book button at a Bookings page, in the Book call-to-action under Magnets. Appointments sync to your leads every few minutes, Teams link included. These pages are connected:</p>
          <div id="intg-mb-businesses">${alertHTML('Loading booking pages…')}</div>
          <div class="intg-actions" style="justify-content:flex-start;margin-top:12px">
            <button type="button" class="intg-btn" id="${p.card.ids.sync}"><span class="material-symbols-outlined" aria-hidden="true">sync</span>Sync now</button>
          </div>
        </div>
      </div>
      ${msbConsentCard()}`;
  }

  if (p.slug === 'calcom') {
    if (!manage) return '';
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">event_available</span> Booking</div>
          <p class="intg-desc" style="margin:0 0 6px">Turn on Cal.com booking per magnet under Magnets, in the Book call-to-action. These event types are available:</p>
          <div id="intg-cc-events">${alertHTML('Loading event types…')}</div>
        </div>
      </div>`;
  }

  if (p.slug === 'callrail') {
    if (!manage) return '';
    return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">call</span> Call tracking</div>
          <p class="intg-desc" style="margin:0 0 6px">Tracked calls land in your Leads inbox with the caller, source, and recording. These tracking numbers are active:</p>
          <div id="intg-r-trackers">${alertHTML('Loading tracking numbers…')}</div>
          <div class="intg-actions" style="justify-content:flex-start;margin-top:12px">
            <button type="button" class="intg-btn" id="${p.card.ids.sync}"><span class="material-symbols-outlined" aria-hidden="true">sync</span>Sync now</button>
            <button type="button" class="intg-btn" id="intg-r-replace"><span class="material-symbols-outlined" aria-hidden="true">key</span>Replace API key</button>
          </div>
          <div id="${p.card.ids.setup}" style="display:none"></div>
        </div>
      </div>`;
  }
  return '';
}

/** The magnet-editor how-to, tucked behind a summary so step 3 stays scannable. */
function msbEditorDisclosure() {
  const ed = WALKTHROUGHS.ms_bookings?.editor;
  if (!ed?.steps?.length) return '';
  return `
    <details class="intg-walkthrough" style="margin-top:8px">
      <summary>${esc(ed.title)}</summary>
      <div class="intg-walkthrough__body">
        <ol>${ed.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
      </div>
    </details>`;
}

/**
 * In-magnet booking as explicit numbered steps with live done/todo state.
 * A self-serve user must always see where they are and what is left; the
 * one thing that must never happen is a silent "connected but the magnet
 * still opens the Microsoft page" with no explanation on this screen.
 */
function msbConsentCard() {
  const approved = state.conn?.app_consent === true;
  const upn = state.conn?.external_account?.upn || '';
  const step = (cls, num, html) => `
    <li class="intg-step${cls}">
      <span class="intg-step__num">${num}</span>
      <span class="intg-step__text">${html}</span>
    </li>`;
  const done = '<span class="material-symbols-outlined" aria-hidden="true">check</span>';
  return `
      <div class="dashboard-card intg-card">
        <div class="card-body">
          <div class="card-title" style="margin-bottom:10px"><span class="material-symbols-outlined" aria-hidden="true">verified_user</span> In-magnet booking</div>
          <p class="intg-desc" style="margin:0 0 6px">Visitors pick a real open time and book inside the magnet itself, no Microsoft page. ${approved ? 'Set up and on for this account.' : 'One step left to turn it on.'}</p>
          <ol class="intg-steps">
            ${step(' intg-step--done', done, `<strong>Connect Microsoft Bookings.</strong> Done${upn ? `, connected as ${esc(upn)}` : ''}.`)}
            ${approved
              ? step(' intg-step--done', done, '<strong>Approve for your organization.</strong> Done. Your Microsoft 365 admin approved in-magnet booking.')
              : step(' intg-step--current', '2', `<strong>Approve for your organization.</strong> A one-time approval on a Microsoft screen. If you are the Microsoft 365 admin it is one click. If not, Microsoft sends the request to your admin. Until it is approved, magnet Book buttons open your Microsoft page in a new tab instead.
                <div class="intg-actions" style="justify-content:flex-start;margin-top:10px">
                  <button type="button" class="intg-btn" id="intg-msb-consent"><span class="material-symbols-outlined" aria-hidden="true">how_to_reg</span>Approve with Microsoft</button>
                </div>`)}
            ${step(approved ? ' intg-step--current' : '', '3', `<strong>Point a magnet at a page.</strong> Under <a href="#/magnets">Magnets</a>, add the Book call-to-action and pick your Bookings page and service.${msbEditorDisclosure()}`)}
          </ol>
        </div>
      </div>`;
}

function fmtDay(tz) {
  if (!tz) return '';
  try { return new Date(tz).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return String(tz).slice(0, 10); }
}

function healthCard(p) {
  if (!state.conn) return '';
  const wh = state.conn.webhook || {};
  const rows = [];
  rows.push(['Status', state.status === 'connected' ? 'Active' : (state.conn.connect_status === 'NEEDS_REAUTH' ? 'Needs reconnecting' : 'Error')]);
  if (p.slug === 'calendly') {
    rows.push(['Booking sync', wh.state === 'active' ? 'On' : 'Off (paid Calendly plan required)']);
    if (wh.last_received_tz) rows.push(['Last booking received', fmtDay(wh.last_received_tz)]);
  }
  if (p.slug === 'calcom') {
    rows.push(['Booking sync', wh.state === 'active' ? 'On' : 'Needs attention']);
    if (wh.last_received_tz) rows.push(['Last booking received', fmtDay(wh.last_received_tz)]);
  }
  if (p.slug === 'ms_bookings') {
    rows.push(['Booking sync', 'Every few minutes (polled)']);
    if (wh.last_swept_tz) rows.push(['Last sweep', fmtDay(wh.last_swept_tz)]);
  }
  if (p.slug === 'callrail') {
    rows.push(['Call sync', wh.state === 'active' ? 'On' : 'Needs attention']);
    if (wh.cookie_capture) rows.push(['Session capture', wh.cookie_capture === 'active' ? 'On' : 'Needs attention']);
    if (wh.last_received_tz) rows.push(['Last call received', fmtDay(wh.last_received_tz)]);
  }
  if (state.conn.created?.created_tz) rows.push(['Connected on', fmtDay(state.conn.created.created_tz)]);

  const warn = (p.slug === 'calendly' && state.status === 'connected' && wh.state !== 'active')
    ? alertHTML('Booking sync is off: webhooks need a paid Calendly plan. Visitors can still book in the magnet; bookings just will not appear on the lead automatically. Reconnect after upgrading to turn it on.', 'error')
    : (p.slug === 'calcom' && state.status === 'connected' && wh.state !== 'active')
      ? alertHTML('Booking sync could not be set up in Cal.com. Visitors can still book; bookings just will not appear on the lead automatically. Reconnect to retry the setup.', 'error')
    : (p.slug === 'callrail' && state.status === 'connected' && wh.state !== 'active')
      ? alertHTML('Automatic call sync could not be set up in CallRail. Calls still arrive via the daily sweep and Sync now. Reconnect to retry the setup.', 'error')
      : '';

  return `
    <div class="dashboard-card intg-card">
      <div class="card-body">
        <div class="card-title" style="margin-bottom:12px"><span class="material-symbols-outlined" aria-hidden="true">monitor_heart</span> Health</div>
        <dl class="intg-kv">
          ${rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join('')}
        </dl>
        ${warn}
      </div>
    </div>`;
}

function dangerCard(p) {
  if (state.status !== 'connected' && state.status !== 'broken') return '';
  if (!canManage()) return '';
  return `
    <div class="dashboard-card intg-card settings-danger">
      <div class="card-body">
        <div class="settings-danger__action">
          <div>
            <div class="settings-item__title">Disconnect ${esc(p.label)}</div>
            <div class="settings-item__desc">${esc(p.disconnect.confirm)}</div>
          </div>
          <button type="button" class="settings-danger__btn" id="${p.card.ids.disconnect}">Disconnect</button>
        </div>
      </div>
    </div>`;
}

// ── wiring + provider extras ─────────────────────────────────────────────────
function wire() {
  const p = state.provider;
  if (!p) return;
  const refresh = load;

  if (p.connect) document.getElementById(p.card.ids.connect)?.addEventListener('click', () => connectProvider(p));
  if (p.slug === 'calcom') {
    document.getElementById(p.card.ids.connect)?.addEventListener('click', () => openCalcomSetup({ refresh }));
    if (state.status === 'connected' && canManage()) loadCalcomEventTypes();
  }
  if (p.slug === 'gohighlevel') {
    document.getElementById(p.card.ids.connect)?.addEventListener('click', () => openGhlSetup({ refresh }));
  }
  if (p.slug === 'ms_bookings' && state.status === 'connected' && canManage()) {
    document.getElementById('intg-msb-consent')?.addEventListener('click', async () => {
      try {
        const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/ms_bookings/adminconsent`);
        const data = await res.json();
        if (data.ok && data.data?.url) { window.location.href = data.data.url; return; }
        throw new Error();
      } catch { toast('Could not start Microsoft approval', 'danger'); }
    });
    loadMsbBusinesses();
    document.getElementById(p.card.ids.sync)?.addEventListener('click', async (ev) => {
      const btn = ev.currentTarget;
      btn.disabled = true;
      try {
        const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/ms_bookings/sync/run`, { method: 'POST' });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || 'sync failed');
        const c = data.data || {};
        toast(c.errors ? `Sync finished with ${c.errors} error${c.errors === 1 ? '' : 's'}.` : `Sync done. ${c.booked || 0} booking${(c.booked || 0) === 1 ? '' : 's'} recorded.`, c.errors ? 'warning' : 'success');
      } catch (e) {
        toast(e.message || 'Sync failed.', 'error');
      } finally {
        btn.disabled = false;
      }
    });
  }
  if (p.slug === 'callrail') {
    document.getElementById(p.card.ids.connect)?.addEventListener('click', () => openCallrailSetup({ refresh }));
    document.getElementById('intg-r-replace')?.addEventListener('click', () => openCallrailSetup({ refresh, replaceKey: true }));
    document.getElementById(p.card.ids.sync)?.addEventListener('click', () => syncCallrail({ refresh }));
    // trackers is MANAGE_MAGNETS-gated server-side (metered external API):
    // never fire it for read-only viewers, it can only 403.
    if (state.status === 'connected' && canManage()) loadTrackers();
  }
  if (p.picker) {
    document.getElementById(p.card.ids.choose)?.addEventListener('click', () => openProviderPicker(p, { refresh }));
    document.getElementById(p.card.ids.sync)?.addEventListener('click', () => syncProvider(p, { refresh }));
  }
  // Conversion-action selection (google_ads only). Same manage-only server
  // gate as trackers/event_types: never fire it for read-only viewers.
  if (p.slug === 'google_ads' && state.status === 'connected' && canManage()) loadConversionActions();
  // event_types has the same manage-only server gate as trackers above.
  if (p.slug === 'calendly' && state.status === 'connected' && canManage()) loadEventTypes();
  if (p.crm && state.status === 'connected' && canManage()) wireCrmSync(p);
  document.getElementById(p.card.ids.disconnect)?.addEventListener('click', () => disconnectProvider(p, { refresh }));
}

// The conversion-action picker: which Google Ads conversion actions count in
// this org's "Other conversions" reporting stream. Selection is opt-in; with
// nothing chosen, Ad Performance shows magnet leads only.
async function loadConversionActions() {
  const el = document.getElementById('intg-g-convactions');
  if (!el) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/google_ads/conversion_actions`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    const actions = data.data.actions || [];
    const selected = new Set(data.data.selected || []);
    if (!actions.length) {
      el.innerHTML = alertHTML('No enabled conversion actions in the linked accounts.');
      return;
    }
    el.innerHTML = `
      <div class="intg-ca-list">
        ${actions.map((a) => `
          <label class="intg-ca-row">
            <input type="checkbox" value="${esc(a.id)}" ${selected.has(a.id) ? 'checked' : ''}>
            <span class="intg-ca-name">${esc(a.name || a.id)}</span>
            ${a.category ? `<span class="intg-ca-cat">${esc(String(a.category).toLowerCase().replace(/_/g, ' '))}</span>` : ''}
          </label>`).join('')}
      </div>
      <div class="intg-actions" style="justify-content:flex-start;margin-top:12px">
        <button type="button" class="intg-btn primary" id="intg-g-ca-save"><span class="material-symbols-outlined" aria-hidden="true">save</span>Save selection</button>
      </div>`;
    // Google Ads accounts can carry a long list of conversion actions. Capped like every
    // other list; the save reads checkboxes from `el`, and hidden inputs are still
    // checked/read, so a selection made before expanding is never silently dropped.
    capList(el.querySelector('.intg-ca-list'), { rowSelector: '.intg-ca-row', noun: 'actions' });

    document.getElementById('intg-g-ca-save')?.addEventListener('click', async () => {
      const btn = document.getElementById('intg-g-ca-save');
      const ids = [...el.querySelectorAll('input[type="checkbox"]:checked')].map((c) => c.value);
      btn.disabled = true;
      try {
        const saveRes = await guardedFetch(`${apiBase()}/m5t/v5/integrations/google_ads/conversion_actions`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversion_action_ids: ids }),
        });
        const saved = await saveRes.json();
        if (!saved.ok) throw new Error(saved.error || 'save failed');
        toast(ids.length ? `Counting ${ids.length} conversion action${ids.length === 1 ? '' : 's'} in reporting.` : 'Conversion counting turned off.', 'success');
      } catch (e) {
        toast(e.message || 'Could not save the selection.', 'error');
      } finally {
        btn.disabled = false;
      }
    });
  } catch {
    el.innerHTML = alertHTML('Could not load conversion actions. If this keeps happening, reconnect.', 'error');
  }
}

// CRM lead-sync section (HubSpot + Zoho): ledger counts + the qualified
// opt-in + Sync now, against /integrations/{slug}/status|config|sync/run.
async function wireCrmSync(p) {
  const el = document.getElementById('intg-crm-status');
  const check = document.getElementById('intg-crm-qualified');
  const syncBtn = document.getElementById(p.card.ids.sync);

  const loadStatus = async () => {
    if (!el) return;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/status`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'load failed');
      const counts = data.data.counts || [];
      const n = (kind, status) => counts.find((c) => c.kind === kind && c.status === status)?.n || 0;
      const line = (label, kind) => {
        const parts = [`${n(kind, 'SENT')} synced`];
        if (n(kind, 'QUEUE')) parts.push(`${n(kind, 'QUEUE')} queued`);
        if (n(kind, 'SENDING')) parts.push(`${n(kind, 'SENDING')} sending`);
        if (n(kind, 'UNCERTAIN')) parts.push(`${n(kind, 'UNCERTAIN')} need confirmation`);
        if (n(kind, 'FAILED')) parts.push(`${n(kind, 'FAILED')} failed`);
        return `
          <div class="intg-ev">
            <span class="intg-ev__name">${esc(label)}</span>
            <span class="intg-ev__meta">${esc(parts.join(' · '))}</span>
          </div>`;
      };
      el.innerHTML = line(p.crm.countLabels.contact, 'contact')
        + (data.data.qualified_sync ? line(p.crm.countLabels.qualified, 'qualified') : '');
      if (counts.some(c => c.status === 'UNCERTAIN' && c.n > 0)) {
        el.innerHTML += alertHTML('Some CRM deliveries need confirmation. Check the CRM before trying again to avoid duplicate records or notes.', 'warning');
      }
      if (check) check.checked = !!data.data.qualified_sync;
    } catch {
      el.innerHTML = alertHTML('Could not load sync status. If this keeps happening, reconnect.', 'error');
    }
  };

  check?.addEventListener('change', async () => {
    // ON by default; turning it OFF degrades every future qualified lead's
    // CRM record, so deselecting confirms first (owner ruling 2026-08-22).
    if (!check.checked) {
      const sure = window.confirm(`Qualified leads will stop updating their status in ${p.label}. Turn this off?`);
      if (!sure) { check.checked = true; return; }
    }
    check.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qualified_sync: check.checked }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'save failed');
      toast(check.checked ? 'Qualified leads now sync their status.' : 'Qualified-lead sync turned off.', 'success');
      loadStatus();
    } catch (e) {
      check.checked = !check.checked;
      toast(e.message || 'Could not save the setting.', 'error');
    } finally {
      check.disabled = false;
    }
  });

  syncBtn?.addEventListener('click', async () => {
    syncBtn.disabled = true;
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/sync/run`, { method: 'POST' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'sync failed');
      const c = data.data || {};
      if (c.uncertain) toast(`${c.uncertain} CRM deliver${c.uncertain === 1 ? 'y needs' : 'ies need'} confirmation. Check the CRM before retrying.`, 'warning');
      else toast(c.failed ? `Sync finished with ${c.failed} error${c.failed === 1 ? '' : 's'}.` : `Sync done. ${c.sent || 0} lead${(c.sent || 0) === 1 ? '' : 's'} pushed.`, c.failed ? 'warning' : 'success');
      loadStatus();
    } catch (e) {
      toast(e.message || 'Sync failed.', 'error');
    } finally {
      syncBtn.disabled = false;
    }
  });

  loadStatus();
}

async function loadMsbBusinesses(q = '') {
  const el = document.getElementById('intg-mb-businesses');
  if (!el) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/ms_bookings/businesses${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    const list = data.data.businesses || [];
    el.innerHTML = list.length
      ? list.map((b) => `
          <div class="intg-ev">
            <span class="intg-ev__name">${esc(b.name)}</span>
            <span class="intg-ev__meta">${esc(b.public_url || '')}</span>
          </div>`).join('')
      : alertHTML('No shared Bookings pages found for this Microsoft account. Note: a Personal booking page ("Bookings with me") cannot connect anywhere, Microsoft has no API for it. Shared booking pages appear when the connected account is on their Staff list. Try the search below with the page\u2019s exact name.')
        + `<div class="intg-msb-search" style="display:flex;gap:8px;margin-top:10px"><input class="mag-modal__input" id="intg-msb-q" type="text" placeholder="Shared page name, e.g. Nicholas Krause"><button class="btn btn-secondary" type="button" id="intg-msb-search">Search</button></div>`;
    capList(el, { rowSelector: '.intg-ev', noun: 'pages' });
    document.getElementById('intg-msb-search')?.addEventListener('click', () => {
      const q = document.getElementById('intg-msb-q')?.value.trim();
      if (q) loadMsbBusinesses(q);
    });
  } catch (e) {
    el.innerHTML = alertHTML('Could not load booking pages. If this keeps happening, reconnect.', 'error');
  }
}

async function loadCalcomEventTypes() {
  const el = document.getElementById('intg-cc-events');
  if (!el) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/calcom/event_types`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    const events = data.data.event_types || [];
    el.innerHTML = events.length
      ? events.map((ev) => `
          <div class="intg-ev">
            <span class="intg-ev__name">${esc(ev.title)}</span>
            <span class="intg-ev__meta">${ev.length ? `${esc(String(ev.length))} min` : ''}</span>
          </div>`).join('')
      : alertHTML('No event types on this Cal.com account yet. Create one in Cal.com and it appears here.');
    capList(el, { rowSelector: '.intg-ev', noun: 'event types' });
  } catch (e) {
    el.innerHTML = alertHTML('Could not load event types. If this keeps happening, reconnect.', 'error');
  }
}

async function loadEventTypes() {
  const el = document.getElementById('intg-c-events');
  if (!el) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/calendly/event_types`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    const events = data.data.event_types || [];
    const link = data.data.scheduling_url
      ? `<div class="intg-ev"><span class="intg-ev__name">Full scheduling page</span><span class="intg-ev__meta">${esc(data.data.scheduling_url)}</span></div>`
      : '';
    el.innerHTML = events.length || link
      ? `${link}${events.map((ev) => `
          <div class="intg-ev">
            <span class="intg-ev__name">${esc(ev.name)}</span>
            <span class="intg-ev__meta">${ev.duration ? `${esc(String(ev.duration))} min` : ''}</span>
          </div>`).join('')}`
      : alertHTML('No active event types on this Calendly account.');
    capList(el, { rowSelector: '.intg-ev', noun: 'event types' });
  } catch (e) {
    el.innerHTML = alertHTML('Could not load event types. If this keeps happening, reconnect.', 'error');
  }
}

async function loadTrackers() {
  const el = document.getElementById('intg-r-trackers');
  if (!el) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail/trackers`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    const trackers = data.data.trackers || [];
    el.innerHTML = trackers.length
      ? trackers.map((t) => `
          <div class="intg-ev">
            <span class="intg-ev__name">${esc(t.name)}</span>
            <span class="intg-ev__meta">${esc(t.number)}${t.type === 'session' ? ' · number pool' : ''}</span>
          </div>`).join('')
      : alertHTML('No active tracking numbers in this company yet. Create one in CallRail and it appears here.');
    capList(el, { rowSelector: '.intg-ev', noun: 'numbers' });
  } catch (e) {
    el.innerHTML = alertHTML('Could not load tracking numbers.', 'error');
  }
}

export default { render, init };
